import { setPadMenuBack } from '../game/gamepad.ts'

/**
 * What's new: the player-facing changelog behind the menu's "What's new" link.
 *
 * Written for players, not from the commit log: what they can now see, do or feel in a match.
 * Fixes to the server, the security boundary, tooling and authoring pipelines stay out of it,
 * and so does anything a player would only notice as the absence of a problem.
 *
 * Newest first. A new entry goes at the top; its date is what lights the dot on the link for
 * anyone who has not opened the list since.
 */

interface Release {
  /** ISO date, used for the unseen dot and shown as "6 Oct". */
  date: string
  title: string
  items: string[]
}

export const RELEASES: Release[] = [
  {
    date: '2026-10-15',
    title: 'Steadier online play',
    items: [
      'The last round in a magazine counts even if you start reloading the instant you fire it. Sniper shots that count went from 69% to 100% in our tests.',
      'Players on a shaky connection keep moving smoothly for everyone else, instead of freezing and then sprinting to catch up.',
      'If your own connection hiccups, the game no longer pulls you back a step once it recovers.',
    ],
  },
  {
    date: '2026-10-14',
    title: 'Smoother online play',
    items: [
      'Shots count where you saw your target. In our tests, on-target assault rifle rounds that count went from 4–14% to 82–90%, and Battle Rifle rounds from 2–19% to 92–97%.',
      'No more rubber-banding: you are no longer pulled back to where you were a moment ago, even mid-jump. Snap-backs of over a metre went from up to 184 a minute on a bad connection to none.',
      'Melee hits land 95% of the time, up from 31–44%. Sword lunges land 94–100% of the time, up from 50–100%.',
      'Splatters count every time you hit someone on your screen, up from 20–83%.',
      'Other players move smoothly instead of stuttering: uneven frames on a typical connection dropped from 52% to 5%.',    ],
  },
  {
    date: '2026-10-13',
    title: 'Credits',
    items: [
      'Credits for the models, textures, sounds and fonts are on the main menu, under the note in the bottom corner.',
    ],
  },
  {
    date: '2026-10-09',
    title: 'Settings and iPad keyboards',
    items: [
      'An iPad with a Magic Keyboard or a mouse now plays with keyboard and mouse instead of touch controls.',
      'The Controls screen is now Settings, with a new Options tab: turn the visor outline off, or choose touch controls yourself.',
    ],
  },
  {
    date: '2026-10-06',
    title: 'Rebindable controls and Cold Storage',
    items: [
      'Rebind every action for keyboard, mouse and controller from the new Controls screen. On phones it is in Touch settings.',
      'Cold Storage gets its full look: Forerunner metal, glowing containment tanks, glass, lights and teleporters, plus the Monitor overhead and Flood forms in the tanks.',
      'The Brute Shot has its own first-person animation, with a grenade chain that swings during the reload and a heavier melee strike.',
      'The Fuel Rod Gun has a new hold and a real reload: the rod pack comes out, and each shot uses one rod.',
      'The SMG, Spiker and Mauler reload their actual magazines.',
      'Spartan armour picks up reflections.',
      'Vehicles and fallen Spartans press into the snow, and the Scorpion\u2019s treads throw up spray and leave ruts.',
    ],
  },
  {
    date: '2026-10-05',
    title: 'Equipment, Capture the Flag and Oddball',
    items: [
      'New equipment: the Bubble Shield and the Regenerator, each with its own HUD icon and effects.',
      'Capture the Flag and Oddball can be picked in custom games, with the Halo 3 flag and the oddball skull. One melee hit with either is a kill.',
      'Eight Halo 3 weapons join the arsenal: Carbine, Mauler, Spiker, Brute Shot, Fuel Rod Gun, Spartan Laser, Flamethrower and a machine gun turret you can tear off its tripod.',
      'New maps: Snowbound, Avalanche and Cold Storage.',
      'The assault rifle uses Halo 3’s firing sound.',
      'The scoreboard shows every player’s ping, and post-game results sit in a slim bar under it.',
    ],
  },
  {
    date: '2026-10-03',
    title: 'Vehicles that take damage',
    items: [
      'Vehicles have health. They show damage as it builds up, wreck, and respawn later.',
      'Rockets, plasma bolts and fuel rods hit vehicles and explode on them instead of passing through.',
      'Grenades bounce off vehicles, come to rest on them, and plasma grenades stick to them.',
      'You can stand on vehicles, and explosions now flip and roll them the way they do in Halo 3.',
      'Vehicles that land upside down can be flipped back over, and wrecks stay where they come down.',
      'Weapon spread, damage and health regeneration now use Halo 3’s numbers. The shotgun fires a Halo 3 cone of pellets that keep flying past close range.',
    ],
  },
  {
    date: '2026-10-01',
    title: 'Matchmaking and medals',
    items: [
      'Find match: one button puts you in the busiest game with room, across Team Slayer, Big Team Battle and free for all.',
      'Team Slayer is played to 30 kills. Big Team Battle is Big Team Slayer, played to 40.',
      'Bots fill matchmade games until enough people arrive.',
      'After a game you see the results, then move on to the next match. Find match never sends you back to a finished game or the one you just left.',
      'Halo 3 medals, with the announcer calling them out.',
      'An update to the game no longer ends your match: you rejoin where you left off.',
      'Your armour wears your team’s colour.',
      'An empty magazine reloads on its own when you have reserve ammo.',
      'Hit detection follows the Spartan’s body as drawn, with a little forgiveness on the edges.',
      'While you sit on the home screen, it tours the maps.',
      'A new energy sword slash, and a muzzle flash on the Warthog’s chain gun.',
    ],
  },
  {
    date: '2026-09-30',
    title: 'Vehicle feel and Blood Gulch at dusk',
    items: [
      'The Ghost and Banshee use Halo 3 engine sounds, engine effects and plasma bolts.',
      'A boost button on phones for the Ghost, Banshee and Chopper.',
      'The Warthog drives like Halo 3: a chevron reticle, a chase camera further back, and Halo-style reverse steering.',
      'Other Spartans are animated for every weapon and action, and their shots come from the gun.',
      'Spartans’ feet step and plant on the ground instead of sliding.',
      'Blood Gulch is set at blue hour, with a twinkling starry sky and the ring lit overhead.',
      'Melee lunges again. Footsteps sound different on each surface, and players grunt when hit.',
      'Pickup prompts float in the world on desktop and appear as buttons on phones.',
    ],
  },
  {
    date: '2026-09-29',
    title: 'The Master Chief arrives',
    items: [
      'Master Chief arms in first person and the Chief’s body in third person, with reworked holds for every weapon.',
      'New weapons: SMG, plasma pistol and plasma rifle. You can now dual wield, and pick up weapons from the ground.',
      'New vehicles: the Mongoose and the Chopper.',
      'Overhauled weapon visuals and relit maps, including new lighting and launcher effects on Guardian.',
      'Phones download smaller files, and the game runs full screen when added to the home screen.',
      'The water on Valhalla displays correctly on phones.',
    ],
  },
  {
    date: '2026-09-25',
    title: 'nothalo.lol',
    items: [
      'The game now lives at nothalo.lol.',
      'A Halo-style death camera follows your body after you die.',
      'Volumetric god rays, glass on Epitaph, and relit arenas.',
      'You no longer fall through the rocks on Valhalla.',
    ],
  },
  {
    date: '2026-09-24',
    title: 'Game modes and Halo 3 arenas',
    items: [
      'Game modes: Free for All, Capture the Flag, Team Ball, Assault and SWAT.',
      'Six Halo 3 arenas: Epitaph, Narrows, Rat’s Nest, Sandtrap, The Pit and Valhalla.',
      'Halo 3 grenades, including plasma grenades that stick to players and vehicles, and new layered explosions.',
      'Halo-style player names over heads, and smoother look gestures on phones.',
      'A weapon-switch animation, plus armour plates on the hands.',
    ],
  },
  {
    date: '2026-09-18',
    title: 'Phones, spawns and Lockout',
    items: [
      'Touch controls rebuilt for phones, with a sensitivity setting that is saved between sessions.',
      'Better spawn points on every map.',
      'Lockout’s collision is rebuilt, so you can walk the map without catching on things.',
      'The Halo 3 low-shield alarm and shield recharge sound.',
      'A faster main menu, a quieter loading screen, and better frame rates on Guardian and Lockout.',
    ],
  },
  {
    date: '2026-09-12',
    title: 'Guardian, the sword and a new Warthog',
    items: [
      'Guardian, with its man cannons.',
      'The shotgun and the energy sword.',
      'A rebuilt Warthog: Halo 3 suspension, more detail, a horn, and handling that drifts through corners and climbs over rocks.',
      'A scoreboard, and touch controls that let you aim and fire at once.',
      'A mascot on the main menu that waves when you tap it.',
    ],
  },
  {
    date: '2026-09-06',
    title: 'Online',
    items: [
      'Multiplayer: play Blood Gulch online with anyone who picks the same match name.',
      'The Warthog, Ghost and Banshee, online.',
      'A main menu with player names, and thumb controls on phones.',
      'Blood Gulch cover and working teleporters, and a full loadout when you respawn.',
    ],
  },
  {
    date: '2026-08-29',
    title: 'First drop',
    items: [
      'Blood Gulch is playable in the browser: move, shoot and fight bots in first person.',
    ],
  },
]

const SEEN_KEY = 'halo.changelog.seen'

const CSS = `
.chg-scrim{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;padding:16px;box-sizing:border-box;
  background:rgba(2,6,12,.72);color:#bfe4ff;font:14px/1.5 ui-sans-serif,system-ui,sans-serif;text-align:left;touch-action:auto}
.chg-scrim[hidden]{display:none}
.chg-panel{width:min(560px,100%);max-height:calc(100dvh - 32px);display:flex;flex-direction:column;box-sizing:border-box;
  background:rgba(6,18,28,.96);border:1px solid #3f8f7d;border-radius:8px;box-shadow:0 18px 60px rgba(0,0,0,.55)}
.chg-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 16px 10px;
  border-bottom:1px solid rgba(63,143,125,.45)}
.chg-head h2{margin:0;font-size:15px;font-weight:600;letter-spacing:.24em;text-transform:uppercase;color:#d8f2ff}
.chg-close{width:36px;height:36px;border:0;border-radius:5px;background:none;color:#bfe4ff;font:inherit;font-size:22px;line-height:1;cursor:pointer}
.chg-close:hover{background:rgba(191,228,255,.08)}
.chg-list{overflow-y:auto;overscroll-behavior:contain;padding:4px 16px 16px;flex:1;min-height:0;-webkit-user-select:text;user-select:text}
.chg-release{padding:14px 0 4px;border-top:1px solid rgba(191,228,255,.06)}
.chg-release:first-child{border-top:0}
.chg-date{margin:0;font-size:11px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:#7fb8a8}
.chg-release h3{margin:2px 0 6px;font-size:15px;font-weight:600;color:#d8f2ff}
.chg-release ul{margin:0;padding-left:18px}
.chg-release li{margin:4px 0;color:#bfe4ff}
.chg-release li::marker{color:#3f8f7d}
.chg-scrim :focus-visible{outline:2px solid #8fd3ff;outline-offset:2px}
.menu button.menu-link.chg-unseen::after{content:'';display:inline-block;width:6px;height:6px;margin-left:6px;vertical-align:2px;
  border-radius:50%;background:#7ff0b0;box-shadow:0 0 6px #7ff0b0}
@media (max-height:480px){.chg-head{padding-top:8px;padding-bottom:6px}}
`

let scrim: HTMLElement | null = null
let opener: HTMLElement | null = null
let style: HTMLStyleElement | null = null

const latest = RELEASES[0].date

function readSeen(): string {
  try { return localStorage.getItem(SEEN_KEY) ?? '' } catch { return '' }
}

function markSeen(): void {
  try { localStorage.setItem(SEEN_KEY, latest) } catch { /* the dot just comes back next visit */ }
}

function injectStyle(): void {
  if (style) return
  style = document.createElement('style')
  style.textContent = CSS
  document.head.append(style)
}

/** "2026-10-06" as "6 Oct", read as a calendar date rather than UTC midnight. */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** Wire the menu's "What's new" link: a dot while there is news the player has not opened. */
export function attachChangelog(link: HTMLElement): void {
  injectStyle()
  link.classList.toggle('chg-unseen', readSeen() < latest)
  link.addEventListener('click', () => {
    link.classList.remove('chg-unseen')
    openChangelog(link)
  })
}

export function openChangelog(from?: HTMLElement): void {
  opener = from ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null)
  markSeen()
  const root = build()
  root.hidden = false
  root.querySelector<HTMLElement>('.chg-list')!.scrollTop = 0
  setPadMenuBack(close)
  addEventListener('keydown', onKey, true)
  root.querySelector<HTMLElement>('.chg-close')!.focus({ preventScroll: true })
}

function close(): void {
  if (!scrim || scrim.hidden) return
  scrim.hidden = true
  setPadMenuBack(null)
  removeEventListener('keydown', onKey, true)
  opener?.focus({ preventScroll: true })
}

function onKey(event: KeyboardEvent): void {
  if (event.code !== 'Escape') return
  event.preventDefault()
  event.stopPropagation()
  close()
}

function build(): HTMLElement {
  if (scrim) return scrim
  injectStyle()
  scrim = document.createElement('div')
  scrim.className = 'chg-scrim'
  scrim.hidden = true
  scrim.setAttribute('role', 'dialog')
  scrim.setAttribute('aria-modal', 'true')
  scrim.setAttribute('aria-labelledby', 'chg-title')
  scrim.innerHTML = `<div class="chg-panel">
    <div class="chg-head"><h2 id="chg-title">What's new</h2><button class="chg-close" type="button" aria-label="Close what's new">×</button></div>
    <div class="chg-list" data-pad-scroll></div>
  </div>`
  const list = scrim.querySelector('.chg-list')!
  for (const release of RELEASES) {
    const section = document.createElement('section')
    section.className = 'chg-release'
    const date = document.createElement('p')
    date.className = 'chg-date'
    date.textContent = shortDate(release.date)
    const title = document.createElement('h3')
    title.textContent = release.title
    const items = document.createElement('ul')
    for (const text of release.items) {
      const item = document.createElement('li')
      item.textContent = text
      items.append(item)
    }
    section.append(date, title, items)
    list.append(section)
  }
  scrim.querySelector('.chg-close')!.addEventListener('click', close)
  scrim.addEventListener('click', event => { if (event.target === scrim) close() })
  document.body.append(scrim)
  return scrim
}
